import * as React from "react";
import set from "lodash/set";
import { Formik, Form, Field } from "formik";
import styled from "styled-components";
import Portal from "components/common/Portal";
import { FormControl, FormGroup, Input, BootstrapModalWrapper, Modal } from "components/bootstrap";
import ModalSubmit from "components/common/ModalSubmit";
import FormikInput from "components/common/FormikInput";
import { Icon, Select, HoverForHelp } from "components/common";
import FrequencySummary from "report/report-configuration-page/FrequencySummary";
import { isWeeklyFrequency, isMonthlyFrequency } from "report/typeGuards/reportingWidget";
import useUserDateTime from "hooks/useUserDateTime";
import FrequencyControls from "./FrequencyControls";
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;
export const FREQUENCY_OPTIONS = {
  HOURLY: {
    value: "HOURLY",
    label: "Hourly"
  },
  DAILY: {
    value: "DAILY",
    label: "Daily"
  },
  WEEKLY: {
    value: "WEEKLY",
    label: "Weekly"
  },
  MONTHLY: {
    value: "MONTHLY",
    label: "Monthly"
  }
};
const frequencyOptions = Object.values(FREQUENCY_OPTIONS);
export const frequencies = Object.keys(FREQUENCY_OPTIONS);
const frequencyHelpText = (frequency) => {
  switch (frequency) {
    case FREQUENCY_OPTIONS.HOURLY.value:
      return "Select the minute of the hour when the report should be sent out.";
    case FREQUENCY_OPTIONS.DAILY.value:
      return "Select the time of the day when the report should be sent out.";
    case FREQUENCY_OPTIONS.WEEKLY.value:
      return "Select the day of the week and time of the day when the report should be sent out.";
    case FREQUENCY_OPTIONS.MONTHLY.value:
      return "Select the day of the month and time of the day when the report should be sent out.";
    default:
      return /* @__PURE__ */ React.createElement("div", null);
  }
};
const validateForm = (values) => {
  var _a, _b, _c;
  let errors = {};
  if (!values.frequency) {
    errors = { frequency: "Frequency is required" };
  }
  if (values.frequency) {
    if (!((_a = values.frequency_configuration) == null ? void 0 : _a.time)) {
      errors = set(errors, "frequency_configuration.time", "Time is required");
    }
    if (isWeeklyFrequency(values) && !((_b = values.frequency_configuration) == null ? void 0 : _b.day_of_week)) {
      errors = set(errors, "frequency_configuration.day_of_week", "Day is required");
    }
    if (isMonthlyFrequency(values) && !((_c = values.frequency_configuration) == null ? void 0 : _c.day_of_month)) {
      errors = set(errors, "frequency_configuration.day_of_month", "Day is required");
    }
  }
  return errors;
};
const resetConfigOnFrequencySelect = (newFrequency, setFieldValue, userTimezone) => {
  const updateFrequencyConfig = (newFrequencyConfig) => setFieldValue("frequency_configuration", newFrequencyConfig);
  switch (newFrequency) {
    case "DAILY":
    case "WEEKLY":
    case "MONTHLY":
      updateFrequencyConfig({ timezone: userTimezone });
      return;
    default:
      updateFrequencyConfig(void 0);
  }
};
const SchedulingFrequencyForm = ({
  schedulingFrequency = void 0,
  onSubmit,
  onCancel,
  title = "Frequency Configuration",
  submitButtonText,
  submitLoadingText
}) => {
  const { userTimezone } = useUserDateTime();
  return /* @__PURE__ */ React.createElement(Portal, null, /* @__PURE__ */ React.createElement(BootstrapModalWrapper, { showModal: true, onHide: onCancel }, /* @__PURE__ */ React.createElement(
    Formik,
    {
      initialValues: schedulingFrequency != null ? schedulingFrequency : {},
      onSubmit,
      validate: validateForm,
      validateOnBlur: false,
      validateOnMount: true
    },
    ({ isSubmitting, isValidating, isValid, values, setFieldValue }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, title)), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(Field, { name: "frequency" }, ({ field: { name, value, onChange, onBlur }, meta: { error, touched } }) => /* @__PURE__ */ React.createElement(
      Input,
      {
        bsStyle: error && touched ? "error" : void 0,
        help: "Select how often this report should be sent out.",
        error: error && touched ? error : void 0,
        id: name,
        label: "Frequency"
      },
      /* @__PURE__ */ React.createElement(
        Select,
        {
          onBlur,
          onChange: (newValue) => {
            if (newValue !== value) {
              resetConfigOnFrequencySelect(newValue, setFieldValue, userTimezone);
            }
            onChange({
              target: { value: newValue, name }
            });
          },
          options: frequencyOptions,
          inputId: name,
          placeholder: "Select a frequency",
          required: true,
          value
        }
      )
    )), values.frequency && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "frequency-options-preview",
        label: `${FREQUENCY_OPTIONS[values.frequency].label} frequency options`,
        help: frequencyHelpText(values.frequency)
      },
      /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(FrequencyControls, null))
    ), /* @__PURE__ */ React.createElement(FormControl.Static, { className: "text-info" }, /* @__PURE__ */ React.createElement(Icon, { name: "info" }), " ", /* @__PURE__ */ React.createElement(
      FrequencySummary,
      {
        frequencyConfiguration: values.frequency_configuration,
        frequency: values.frequency
      }
    )), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        id: "frequency_configuration.override_widgets_timerange",
        name: "frequency_configuration.override_widgets_timerange",
        type: "checkbox",
        label: "Override widget time ranges with selected frequency"
      }
    ), /* @__PURE__ */ React.createElement(HoverForHelp, { title: "Override time ranges" }, "This action will override time ranges in all widgets with the selected frequency")))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
      ModalSubmit,
      {
        onCancel,
        disabledSubmit: isValidating || !isValid,
        isSubmitting,
        isAsyncSubmit: true,
        submitLoadingText,
        submitButtonText
      }
    )))
  )));
};
export default SchedulingFrequencyForm;
